import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { BellRinging, X } from '@phosphor-icons/react';

import { getPushPreview } from '@/api/pushlog';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';

// PushPreviewDialog shows what one push recipient saw on their device. Unlike
// the email preview it doesn't re-render through a templates registry — push
// title/body are pre-rendered literal strings at send time (notify.Create
// takes the final text), so the API just returns the stored copy.

export interface PushPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  token: string | null;
  messageId: string | null;
}

export function PushPreviewDialog({
  open,
  onOpenChange,
  slug,
  token,
  messageId,
}: PushPreviewDialogProps) {
  const { t } = useTranslation('common');

  const previewQuery = useQuery({
    queryKey: ['push-preview', slug, messageId],
    queryFn: () => getPushPreview(token, slug, messageId!),
    enabled: open && !!token && !!messageId,
    staleTime: 60_000,
  });

  const data = previewQuery.data;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-foreground/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[81] flex max-h-[88vh] w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
        >
          <header className="flex items-start gap-3 border-b border-border px-6 py-4">
            <span
              aria-hidden
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/70"
            >
              <BellRinging size={18} weight="duotone" />
            </span>
            <div className="flex-1 overflow-hidden">
              <DialogPrimitive.Title className="truncate font-heading text-lg font-bold leading-tight">
                {t('settings.log.push.preview.title')}
              </DialogPrimitive.Title>
              {data && (
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {(data.display_name || data.email || '').trim()}
                  {data.platform ? ` · ${data.platform}` : ''}
                </p>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="ghost" size="icon" aria-label={t('cancel')}>
                <X weight="bold" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <div className="flex-1 overflow-auto px-6 py-5">
            {previewQuery.isPending && (
              <p className="text-sm text-muted-foreground">
                {t('settings.log.push.preview.loading')}
              </p>
            )}
            {previewQuery.isError && (
              <p className="text-sm text-foreground">{renderPushPreviewError(previewQuery.error, t)}</p>
            )}
            {data && (
              <article className="rounded-2xl border border-border bg-background p-5 shadow-md">
                <h3 className="font-heading text-base font-bold leading-snug">{data.title || t('settings.log.push.preview.untitled')}</h3>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/85">{data.body}</p>
                {data.action_url && (
                  <p className="mt-3 truncate font-mono text-[0.7rem] text-muted-foreground">
                    {t('settings.log.push.preview.actionUrl')}: {data.action_url}
                  </p>
                )}
              </article>
            )}
          </div>

          <footer className="flex items-center justify-end gap-3 border-t border-border px-6 py-3">
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="outline">
                {t('common.close')}
              </Button>
            </DialogPrimitive.Close>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function renderPushPreviewError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError && err.code === 'not_found') {
    return t('settings.log.push.preview.error.notFound');
  }
  return t('settings.log.push.preview.error.unknown');
}
